/** @module Platform/FireOSPlayer */

import PlayerActions from '../enums/PlayerActions';
import PlayerData from '../enums/PlayerData';
import _ from 'underscore';
import * as Platform from '../Platform';
import { Networkable } from '../mixins/Networkable';
import { BrowserReloadable } from '../mixins/BrowserReloadable';
import { LogPersistable } from '../mixins/LogPersistable';
import { ApplicationStateQueryable } from '../mixins/ApplicationStateQueryable';
import { Deferred } from '../../Utility/Deferred';
import { addCustomTapListener } from '../../Utility/CustomTapListener';
import { DeviceLog, PlayerResponseData } from '../types';
import { IframeMessengerPlayerAbstract } from '../IframeMessengerPlayerAbstract';
import { LogPersistenceInterface } from '../interfaces/LogPersistenceInterface';
import { NetworkInterface } from '../interfaces/NetworkInterface';
import { ReloadInterface } from '../interfaces/ReloadInterface';
import { ApplicationStateInterface } from '../interfaces/ApplicationStateInterface';
import { DeviceInformationInterface } from '../interfaces/DeviceInformationInterface';
import { DeviceInformationQueryable } from '../mixins/DeviceInformationQueryable';
import { SystemInformationInterface } from '../interfaces/SystemInformationInterface';
import { SystemInformationQueryable } from '../mixins/SystemInformationQueryable';
import { ApplicationRestartable } from '../mixins/ApplicationRestartable';
import { LogInterface } from '../interfaces/LogInterface';
import { isUrlQueryPlatform } from '../PlatformCheck';

/**
 * Player for FireOS platform
 *
 * @implements module:Platform.NamedValueCacheInterface
 * @implements module:Platform.RestartApplicationInterface
 * @implements module:Platform.ScreenshotInterface
 */
class FireOSPlayer
    extends DeviceInformationQueryable(
        ApplicationRestartable(
            SystemInformationQueryable(
                ApplicationStateQueryable(
                    LogPersistable(Networkable(BrowserReloadable(IframeMessengerPlayerAbstract)))
                )
            )
        )
    )
    implements
        LogPersistenceInterface,
        LogInterface,
        NetworkInterface,
        ReloadInterface,
        ApplicationStateInterface,
        DeviceInformationInterface,
        SystemInformationInterface
{
    constructor() {
        super(FireOSPlayer.getTargetOrigin());
        this.name = 'Amazon Fire OS';
        this.type = Platform.FIRE_OS;
    }

    initializePlatform(): Promise<void> {
        this._setupSettingsListener();
        return Promise.resolve();
    }

    getHostPlatform(): Promise<string> {
        return Promise.resolve(this.name);
    }

    getNamedValue<T extends PlayerResponseData>(key: string, defaultValue: T): Deferred<T> {
        return this._invokePlayerMethod(PlayerActions.GET_NAMED_VALUE, {
            key: key,
            value: defaultValue,
        });
    }

    setNamedValue<T extends PlayerResponseData>(key: string, value: T): Deferred<T> {
        return this._invokePlayerMethod(PlayerActions.SET_NAMED_VALUE, { key, value });
    }

    deleteNamedValue(key: string): Deferred<void> {
        return this._invokePlayerMethod(PlayerActions.DELETE_NAMED_VALUE, { key });
    }

    _generateAndGetHardwareId(): Promise<string> {
        return this.getDeviceInformation().then(function (deviceInfo) {
            return (
                'Amazon://' + deviceInfo.platform + ':' + deviceInfo.model + ':' + deviceInfo.serial
            );
        });
    }

    getPlayerVersion(): PromiseLike<string> {
        return this._invokePlayerMethod<string>(PlayerActions.GET_PLAYER_VERSION);
    }

    getScreenshot(): Deferred<string> {
        return this._invokePlayerMethod(PlayerActions.GET_SCREENSHOT);
    }

    getPlatformLogs(): Deferred<DeviceLog> {
        return this._getData(PlayerData.PLATFORM_LOGS);
    }

    /**
     * Checks if the device is running FireOS
     */
    static isTheRunningPlatform(): boolean {
        const thisPlatformIsRunning = isUrlQueryPlatform('FireOS');
        if (typeof thisPlatformIsRunning === 'boolean') {
            return thisPlatformIsRunning;
        }

        // use fallback check
        const userAgent = navigator.userAgent;

        const models = [
            /**
             * Fire TV Sticks
             */
            'AFTM',
            'AFTT',
            'AFTMM',
            'AFTSSS', // 3rd Generation Fire TV Stick

            /**
             * Amazon Pendant
             */
            'AFTN',

            // Cube
            'ATFA',

            // Toshiba
            'AFTKMST12',

            /**
             * Not tested
             */
            'AFTB',
            'AFTS',
            'AFTR',
            'AFTEUFF014',
            'AFTEU011',
            'AFTEU014',
            'AFTJMST12',
            'AFTEAMR311',
            'AFTSO001',
            'AFTBAMR311',
            'AFTRS',
            'AFTLE',
            'AFTTIFF55',
            'AFTWI001',
            'AFTDCT31',
            'AFTBU001',
            'AFTPR001',
            'AFTSS',
            'AFTKA', // 4k MAX
            'AFTWMST22',

            /**
             * Fire Tablets
             */
            //tested
            'KFMUWI',

            //not tested
            'KFAUWI',
            'KFMUWI',
            'KFSUWI',
            'KFMAWI',
            'KFKAWI',
            'KFDOWI',
            'KFARWI',
            'KFASWI',
            'KFSAWA',
            'KFSAWI',
            'KFTBWI',
            'KFMEWI',
            'KFFOWI',
            'KFGIWI',
            'KFONWI',
            'KFTRWI',
            'KFTRPWI',

            /**
             * Not supported
             */
            // 'KFSAW',
            // 'KFAPW',
            // 'KFTHW',
            // 'KFSOWI',
            // 'KFJW',
            // 'KFTT',
            // 'KFOT',
            'Kindle Fire',
        ];

        const modelIndex = _.findIndex(models, function (model) {
            return userAgent.indexOf(model) !== -1;
        });

        if (modelIndex !== -1) {
            return true;
        }

        const devices = ['FIRETV', 'FIRETVSTICK', 'FIRETABLET'];

        // For newer versions of the app we can directly look for platform names
        const deviceIndex = _.findIndex(devices, function (device) {
            return userAgent.indexOf(device) !== -1;
        });

        if (deviceIndex !== -1) {
            return true;
        }

        return false;
    }

    /**
     * Gets the targetOrigin based on cordova version
     * cordova 10 uses https://localhost protocol to serve html files which is more secure
     * Target Origin is the origin url of the root html page
     */
    static getTargetOrigin(): string {
        const userAgent = navigator.userAgent;
        const cordovaKey = 'cordova:10.1.1';
        const index = userAgent.indexOf(cordovaKey);

        // if useragent does not have cordova version information, it is an older version of app.
        // So, use file:// as target origin
        if (index === -1) {
            return 'file://';
        }

        return 'https://localhost';
    }

    /**
     * Opens settings page on triple click of page body
     * We need this fallback in case the device uses touchscreen
     */
    private _setupSettingsListener(): void {
        addCustomTapListener(
            () => this._performPlayerAction(PlayerActions.TOGGLE_SETTINGS_OVERLAY),
            document.getElementsByTagName('body')[0],
            3
        );
    }
}

export { FireOSPlayer };
